import { IconChevronLeft } from '@aws-amplify/ui-react/internal'
import { Box } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import ButtonBar from '@/components/inputs/ButtonBar'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { useAlertContext } from '@/contexts/AlertContext'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import BidOverviewButton from '@/features/bidding/components/BidOverviewButton'
import BidConfirmSendButton from '@/features/bidding/components/capacity/confirm/BidConfirmSendButton'
import CountrySelector from '@/features/bidding/components/CountrySelector'
import BidConfirmPriceDataGrid from '@/features/bidding/components/price/confirm/BidConfirmPriceDataGrid'
import { BidOverviewDrawerContextProvider } from '@/features/bidding/contexts/BidOverviewDrawerContextProvider'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import { useBidOfferConfirmPriceData } from '@/features/bidding/hooks/useBidOfferConfirmPriceData'
import { useMarketClosingWarning } from '@/features/bidding/hooks/useMarketClosingWarning'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import { buildBidCapacityLink, buildBidPriceLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

const BidOfferPriceConfirmContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pushAlert } = useAlertContext()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const { markPtuAsReset } = useResetPtusContext()

  const { showMarketClosingWarning } = useMarketClosingWarning()

  const { isLoading, data } = useBidOfferConfirmPriceData()

  // Store the previous fixed bids to detect real-time PTU expirations
  const previousFixedBidsRef = useRef<ExtendedBid[] | null>(null)

  useEffect(() => {
    if (data) {
      showMarketClosingWarning(data.groupParams.deliveryDay, data.groupParams.marketProgram, data.groupParams.bidType)
    }
  }, [data?.groupParams.deliveryDay.valueOf(), data?.groupParams.marketProgram, data?.groupParams.bidType])

  // Mark PTUs as reset when they expire in real-time
  // By comparing previous fixed bids with current fixed bids, we detect when a PTU's value
  // changes due to market closure. This avoids false positives on initial page load.
  useEffect(() => {
    if (!data) return
    if (!previousFixedBidsRef.current) {
      // Store current as previous for next comparison
      previousFixedBidsRef.current = data.fixedActivationGroupBids
      return
    }

    const previousFixedBids = previousFixedBidsRef.current

    data.fixedActivationGroupBids.forEach((currentBid) => {
      const previousBid = previousFixedBids.find((bid) => bid.activationGroupUuid === currentBid.activationGroupUuid)
      if (!previousBid) return

      for (const currentPtu of currentBid.ptus) {
        const previousPtu = previousBid.ptus.find((ptu) => ptu.ptu.start.equals(currentPtu.ptu.start))
        if (!previousPtu) return

        // Check if this PTU's volume changed (was reset due to expiration)
        const wasReset = previousPtu.offeredVolume.quantity !== currentPtu.offeredVolume.quantity

        if (wasReset) {
          // Try to mark this PTU as reset, only succeeds if PTU expired during this session
          const wasMarkedAsReset = markPtuAsReset({
            activationGroupUuid: currentBid.activationGroupUuid,
            ptuStart: currentPtu.ptu.start,
            bidType: currentBid.bidType,
            marketProgram: currentBid.marketProgram,
          })
          // Only show alert if the PTU was actually marked (expired during session, not before)
          if (wasMarkedAsReset) {
            pushAlert({
              severity: 'warning',
              message: t('bidding.create_bid.ptu_reset_alert.message', {
                ptu: currentPtu.ptu.start.toFormat('HH:mm'),
                activationGroup: currentBid.activationGroupName,
              }),
              title: t('bidding.create_bid.ptu_reset_alert.title', { ptu: currentPtu.ptu.start.toFormat('HH:mm') }),
            })
          }
        }
      }
    })

    // Store current as previous for next comparison
    previousFixedBidsRef.current = data.fixedActivationGroupBids
  }, [data?.fixedActivationGroupBids])

  if (isLoading || !data) return <FullPageSpinner />

  return (
    <>
      <PageHeader
        appBarContent={
          <Box alignItems="center" display="flex" flexDirection="row" justifyContent="space-between" width="100%">
            <CountrySelector disabled />
            <BidOverviewButton />
          </Box>
        }
        breadcrumbItems={[
          { text: t('component.page_header.bidding'), to: '/bidding' },
          { text: t('component.page_header.bid.todo_list'), to: '/bidding/todo-list' },
          {
            text: t('component.page_header.bid.create.label', {
              portfolio: data.groupParams.portfolioCode,
              marketProgram: t(`bidding.market_program.${data.groupParams.marketProgram}`),
              bidType: data.groupParams.bidType.toLowerCase(),
            }),
            to: buildBidCapacityLink(data.groupParams),
          },
          { text: t('bidding.create_bid.price_bid'), to: buildBidPriceLink(data.groupParams) },
        ]}
        pageTitle={t('component.page_header.bid.confirmation.label')}
      />

      <BidConfirmPriceDataGrid activationGroupBids={data.fixedActivationGroupBids} />

      <ButtonBar>
        <CustomButton
          key="backButton"
          startIcon={<IconChevronLeft />}
          variant={'outlined'}
          onClick={() => navigate(-1)}
        >
          {t('common.navigation.back')}
        </CustomButton>

        {canMutateBidding && (
          <BidConfirmSendButton
            bids={data.fixedActivationGroupBids}
            eligibleActivationGroups={data.eligibleActivationGroups}
          />
        )}
      </ButtonBar>
    </>
  )
}

const BidOfferPriceConfirm = () => {
  return (
    <BidOverviewDrawerContextProvider>
      <BidOfferPriceConfirmContent />
    </BidOverviewDrawerContextProvider>
  )
}

export default BidOfferPriceConfirm
